import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import AcademiaSelect from '../../common/AcademiaSelect';
import { useAdminApprenantDetail } from '../../../hooks/useAcademiaAdmin';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { formatMoney } from '../../../utils/currency';
import { updateUtilisateurTypeProfilParrain } from '../../../services/parrainageService';
import { AcademicPanelCard, AcademicPanelTableWrap } from './AcademicPanelCard';

const TYPE_PROFIL_PARRAIN_OPTIONS = [
  { value: '', label: 'Automatique (selon profil)' },
  { value: 'APPRENANT', label: 'Apprenant' },
  { value: 'FORMATEUR', label: 'Formateur' },
  { value: 'ENSEIGNANT_PARTENAIRE', label: 'Enseignant partenaire' },
  { value: 'INSTITUTION_PARTENAIRE', label: 'Institution partenaire' },
  { value: 'FONCTIONNAIRE', label: 'Fonctionnaire' },
  { value: 'AUTORITE_HIERARCHIQUE', label: 'Autorité hiérarchique' },
];

function formatPersonName(profil?: { prenom?: string; nom?: string; postnom?: string } | null) {
  if (!profil) return '—';
  return [profil.prenom, profil.nom, profil.postnom].filter(Boolean).join(' ') || '—';
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR');
}

function toAmount(value: unknown) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function DetailGrid({
  rows,
  dense,
}: {
  rows: { label: string; value: ReactNode }[];
  dense?: boolean;
}) {
  return (
    <dl
      className={['academic-panel-detail-grid', dense ? 'academic-panel-detail-grid--dense' : ''].join(' ')}
    >
      {rows.map((row) => (
        <div key={row.label} className="academic-panel-detail-cell">
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PaginatedTableSection<T>({
  icon,
  title,
  items,
  emptyLabel,
  columns,
  renderRow,
  variant = 'default',
}: {
  icon: string;
  title: string;
  items: T[];
  emptyLabel: string;
  columns: { header: string; className?: string }[];
  renderRow: (row: T) => ReactNode;
  variant?: 'default' | 'accent' | 'preview' | 'success';
}) {
  const { paginatedItems, currentPage, totalPages, onPageChange, summary } = useAdminTablePagination(items);

  return (
    <AcademicPanelCard
      icon={icon}
      title={title}
      variant={variant}
      flush
      badge={
        items.length > 0 ? (
          <span className="academic-panel-badge">{items.length}</span>
        ) : null
      }
    >
      {items.length === 0 ? (
        <p className="academic-panel-empty">{emptyLabel}</p>
      ) : (
        <>
          <AcademicPanelTableWrap>
            <table className="academic-panel-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.header} className={col.className}>
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{paginatedItems.map((row) => renderRow(row))}</tbody>
            </table>
          </AcademicPanelTableWrap>
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={summary}
          />
        </>
      )}
    </AcademicPanelCard>
  );
}

export default function AcademiaApprenantDetailPanel() {
  const navigate = useNavigate();
  const { apprenantId = '' } = useParams<{ apprenantId: string }>();
  const { data, isLoading, isError, errorMessage, refetch } = useAdminApprenantDetail(apprenantId);
  const [typeParrainBusy, setTypeParrainBusy] = useState(false);
  const [typeParrainMsg, setTypeParrainMsg] = useState<string | null>(null);
  useRegisterPortalRefresh(refetch);

  const displayName = useMemo(() => {
    if (!data) return 'Fiche apprenant';
    return formatPersonName(data.profil) !== '—' ? formatPersonName(data.profil) : data.telephone || 'Apprenant';
  }, [data]);

  const flatPresences = useMemo(() => {
    if (!data?.inscriptions) return [];
    return data.inscriptions.flatMap(
      (ins: {
        filiere?: { libelle?: string };
        cohorte?: { code?: string };
        presences?: Record<string, unknown>[];
      }) =>
        (ins.presences ?? []).map((p) => ({
          ...p,
          cohorteLabel: [ins.filiere?.libelle, ins.cohorte?.code].filter(Boolean).join(' · '),
        })),
    );
  }, [data]);

  const flatNotes = useMemo(() => {
    if (!data?.inscriptions) return [];
    return data.inscriptions.flatMap(
      (ins: {
        filiere?: { libelle?: string };
        cohorte?: { code?: string };
        notes?: Record<string, unknown>[];
      }) =>
        (ins.notes ?? []).map((n) => ({
          ...n,
          cohorteLabel: [ins.filiere?.libelle, ins.cohorte?.code].filter(Boolean).join(' · '),
        })),
    );
  }, [data]);

  const flatRecus = useMemo(() => {
    if (!data?.inscriptions) return [];
    return data.inscriptions.flatMap((ins: { recus?: Record<string, unknown>[] }) => ins.recus ?? []);
  }, [data]);

  const sideCard = useMemo(() => {
    if (isLoading) {
      return {
        label: 'Dossier financier',
        value: '…',
        helper: null,
        sideLinks: [{ label: 'Annuaire', to: '/admin/apprenants', icon: 'group' }],
      };
    }
    const inscriptions = (data?.inscriptions ?? []) as {
      montantPayeCdf?: number | string;
      montantTotalCdf?: number | string;
    }[];
    let paid = 0;
    let total = 0;
    for (const ins of inscriptions) {
      paid += toAmount(ins.montantPayeCdf);
      total += toAmount(ins.montantTotalCdf);
    }
    const reste = Math.max(0, total - paid);
    return {
      label: 'Total payé',
      value: formatMoney(paid, 'CDF'),
      helper: `${inscriptions.length} inscription(s) · reste ${formatMoney(reste, 'CDF')}`,
      sideLinks: [
        { label: 'Inscriptions', to: '/admin/inscriptions', icon: 'assignment' },
        { label: 'Annuaire', to: '/admin/apprenants', icon: 'group' },
      ],
    };
  }, [isLoading, data]);

  return (
    <AdminPageShell
      title={displayName}
      sideCard={sideCard}
      description={
        data?.profilApprenant?.matricule
          ? `Matricule ${data.profilApprenant.matricule} — vue consolidée du dossier`
          : 'Vue consolidée du dossier apprenant'
      }
      onRetry={refetch}
      isLoading={isLoading}
      isError={isError}
      errorMessage={errorMessage ?? 'Impossible de charger la fiche apprenant.'}
      loadingMessage="Chargement de la fiche…"
      primaryAction={
        <Link to="/admin/apprenants" className="academic-panel-btn academic-panel-btn--secondary">
          <LucideSymbolIcon name="arrow_back" tone="inherit" size={14} />
          Retour à l&apos;annuaire
        </Link>
      }
    >
      {data ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-3">
            {data.profilApprenant?.matricule ? (
              <p className="academic-panel-stat-banner academic-panel-stat-banner--info m-0 flex-1 min-w-[12rem]">
                <span className="inline-flex items-center gap-2">
                  <LucideSymbolIcon name="badge" tone="inherit" size={16} />
                  Matricule{' '}
                  <strong className="font-mono">{data.profilApprenant.matricule}</strong>
                </span>
              </p>
            ) : null}
            {data.portefeuille ? (
              <p className="academic-panel-stat-banner m-0 flex-1 min-w-[12rem]">
                <span className="inline-flex items-center gap-2">
                  <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={16} />
                  Solde{' '}
                  <strong className="tabular-nums">
                    {formatMoney(data.portefeuille.solde, data.portefeuille.devise || 'CDF')}
                  </strong>
                </span>
              </p>
            ) : null}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <AcademicPanelCard icon="badge" title="Compte & identité" variant="preview">
              <DetailGrid
                dense
                rows={[
                  { label: 'Téléphone', value: data.telephone || '—' },
                  { label: 'Email', value: data.email || '—' },
                  { label: 'Rôle', value: data.role || '—' },
                  { label: 'Compte actif', value: data.estCompteActif ? 'Oui' : 'Non' },
                  { label: 'Code parrainage', value: data.codeParrainage || '—' },
                  {
                    label: 'Type profil parrain',
                    value: (
                      <div className="space-y-2">
                        <AcademiaSelect
                          fieldSize="sm"
                          className="max-w-md"
                          disabled={typeParrainBusy}
                          value={data.typeProfilParrain ?? ''}
                          onChange={async (e) => {
                            setTypeParrainBusy(true);
                            setTypeParrainMsg(null);
                            try {
                              await updateUtilisateurTypeProfilParrain(
                                apprenantId,
                                e.target.value || null,
                              );
                              setTypeParrainMsg('Type parrain mis à jour.');
                              await refetch();
                            } catch (err) {
                              setTypeParrainMsg(
                                err instanceof Error ? err.message : 'Mise à jour impossible',
                              );
                            } finally {
                              setTypeParrainBusy(false);
                            }
                          }}
                        >
                          {TYPE_PROFIL_PARRAIN_OPTIONS.map((o) => (
                            <option key={o.value || 'auto'} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </AcademiaSelect>
                        {typeParrainMsg ? (
                          <span className="text-xs font-medium text-on-surface-variant">{typeParrainMsg}</span>
                        ) : null}
                      </div>
                    ),
                  },
                  { label: 'Vérification', value: data.profil?.statutVerificationCompte || '—' },
                  { label: 'Créé le', value: formatDate(data.dateCreation) },
                  { label: 'Dernière mise à jour', value: formatDate(data.dateMiseAJour) },
                ]}
              />
            </AcademicPanelCard>

            <AcademicPanelCard icon="person" title="Profil personnel">
              <DetailGrid
                rows={[
                  { label: 'Nom complet', value: formatPersonName(data.profil) },
                  { label: 'Sexe', value: data.profil?.sexe || '—' },
                  {
                    label: 'Date de naissance',
                    value: data.profil?.dateNaissance ? formatDate(data.profil.dateNaissance) : '—',
                  },
                  { label: 'Mode paiement préféré', value: data.profil?.modePaiementPrefere || '—' },
                  {
                    label: 'Notifications push',
                    value: data.profil?.notificationsPush ? 'Activées' : 'Désactivées',
                  },
                ]}
              />
            </AcademicPanelCard>
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            {data.profilApprenant ? (
              <AcademicPanelCard icon="school" title="Profil académique" variant="accent">
                <DetailGrid
                  rows={[
                    { label: 'Matricule', value: data.profilApprenant.matricule || '—' },
                    {
                      label: 'Catégorie',
                      value: (
                        <span className="academic-panel-badge academic-panel-badge--muted">
                          {String(data.profilApprenant.categorie || '—').replace(/_/g, ' ')}
                        </span>
                      ),
                    },
                    { label: 'Raison catégorisation', value: data.categorisationRaison || '—' },
                    { label: 'Profession', value: data.profilApprenant.profession || '—' },
                    { label: 'Employeur', value: data.profilApprenant.employeur || '—' },
                    { label: 'Institution', value: data.profilApprenant.institution?.nom || '—' },
                  ]}
                />
              </AcademicPanelCard>
            ) : null}

            {data.portefeuille ? (
              <AcademicPanelCard icon="account_balance_wallet" title="Portefeuille" variant="success">
                <DetailGrid
                  rows={[
                    {
                      label: 'Solde disponible',
                      value: (
                        <span className="text-base font-black text-primary tabular-nums">
                          {formatMoney(data.portefeuille.solde, data.portefeuille.devise || 'CDF')}
                        </span>
                      ),
                    },
                    { label: 'Devise', value: data.portefeuille.devise || 'CDF' },
                  ]}
                />
              </AcademicPanelCard>
            ) : null}
          </div>

          <PaginatedTableSection
            icon="assignment"
            title="Inscriptions"
            items={data.inscriptions ?? []}
            emptyLabel="Aucune inscription."
            columns={[
              { header: 'Formation' },
              { header: 'Statut' },
              { header: 'Total', className: 'text-right' },
              { header: 'Payé', className: 'text-right' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => {
              const filiere = row.filiere as { libelle?: string } | undefined;
              const cohorte = row.cohorte as { code?: string } | undefined;
              return (
                <tr key={String(row.id)}>
                  <td>
                    <div className="font-semibold text-slate-900">{filiere?.libelle || '—'}</div>
                    <div className="text-xs text-on-surface-variant">{cohorte?.code || '—'}</div>
                  </td>
                  <td>
                    <span className="academic-panel-badge academic-panel-badge--muted">
                      {String(row.statut || '—').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="text-right tabular-nums">
                    {formatMoney(row.montantTotalCdf as string | number, 'CDF')}
                  </td>
                  <td className="text-right font-semibold tabular-nums text-primary">
                    {formatMoney(row.montantPayeCdf as string | number, 'CDF')}
                  </td>
                  <td className="text-xs text-on-surface-variant">
                    {formatDate(row.dateInscription as string)}
                  </td>
                </tr>
              );
            }}
          />

          <PaginatedTableSection
            icon="payments"
            title="Transactions"
            items={data.transactions ?? []}
            emptyLabel="Aucune transaction."
            columns={[
              { header: 'Référence' },
              { header: 'Formation' },
              { header: 'Montant', className: 'text-right' },
              { header: 'Mode' },
              { header: 'Statut' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => {
              const ins = row.inscription as { filiere?: { libelle?: string }; cohorte?: { code?: string } } | undefined;
              return (
                <tr key={String(row.id)}>
                  <td className="font-mono text-xs">{String(row.reference || row.id).slice(0, 12)}…</td>
                  <td className="text-xs">
                    {ins?.filiere?.libelle || '—'}
                    {ins?.cohorte?.code ? ` (${ins.cohorte.code})` : ''}
                  </td>
                  <td className="text-right font-semibold tabular-nums">
                    {formatMoney(row.montant as string | number, String(row.devise || 'CDF'))}
                  </td>
                  <td>{String(row.modePaiement || '—').replace(/_/g, ' ')}</td>
                  <td>{String(row.statut || '—')}</td>
                  <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
                </tr>
              );
            }}
          />

          <PaginatedTableSection
            icon="receipt"
            title="Reçus de paiement"
            items={flatRecus}
            emptyLabel="Aucun reçu."
            columns={[
              { header: 'N° reçu' },
              { header: 'Montant', className: 'text-right' },
              { header: 'Statut' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => (
              <tr key={String(row.id)}>
                <td className="font-mono text-xs">{row.numeroRecu as string}</td>
                <td className="text-right tabular-nums">{formatMoney(row.montant as string | number, 'CDF')}</td>
                <td>{String(row.statut || '—')}</td>
                <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
              </tr>
            )}
          />

          <div className="grid gap-5 xl:grid-cols-2">
            <PaginatedTableSection
              icon="event_available"
              title="Présences"
              items={flatPresences}
              emptyLabel="Aucune présence enregistrée."
              columns={[
                { header: 'Formation' },
                { header: 'Date séance' },
                { header: 'Statut' },
              ]}
              renderRow={(row: Record<string, unknown>) => (
                <tr key={String(row.id)}>
                  <td className="text-xs">{row.cohorteLabel as string}</td>
                  <td>{formatDate(row.dateSeance as string)}</td>
                  <td>{String(row.statut || '—')}</td>
                </tr>
              )}
            />

            <PaginatedTableSection
              icon="grade"
              title="Notes & évaluations"
              items={flatNotes}
              emptyLabel="Aucune note."
              columns={[
                { header: 'Formation' },
                { header: 'Évaluation' },
                { header: 'Note /20', className: 'text-right' },
                { header: 'Date' },
              ]}
              renderRow={(row: Record<string, unknown>) => (
                <tr key={String(row.id)}>
                  <td className="text-xs">{row.cohorteLabel as string}</td>
                  <td>{row.libelle as string}</td>
                  <td className="text-right font-bold tabular-nums text-primary">{row.noteSur20 as string}</td>
                  <td className="text-xs text-on-surface-variant">{formatDate(row.dateEvaluation as string)}</td>
                </tr>
              )}
            />
          </div>

          <PaginatedTableSection
            icon="account_balance"
            title="Mouvements portefeuille"
            items={data.mouvementsWallet ?? []}
            emptyLabel="Aucun mouvement."
            columns={[
              { header: 'Type' },
              { header: 'Direction' },
              { header: 'Montant', className: 'text-right' },
              { header: 'Statut' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => (
              <tr key={String(row.id)}>
                <td>{String(row.type || '—').replace(/_/g, ' ')}</td>
                <td>{row.direction as string}</td>
                <td className="text-right tabular-nums">
                  {formatMoney(row.montant as string | number, String(row.devise || 'CDF'))}
                </td>
                <td>{row.statut as string}</td>
                <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
              </tr>
            )}
          />

          <PaginatedTableSection
            icon="report"
            title="Réclamations"
            items={data.reclamations ?? []}
            emptyLabel="Aucune réclamation."
            columns={[
              { header: 'Référence' },
              { header: 'Type' },
              { header: 'Statut' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => (
              <tr key={String(row.id)}>
                <td className="font-mono text-xs">{row.reference as string}</td>
                <td>{String(row.type || '—').replace(/_/g, ' ')}</td>
                <td>{row.statut as string}</td>
                <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
              </tr>
            )}
          />

          <div className="grid gap-5 xl:grid-cols-2">
            <PaginatedTableSection
              icon="group"
              title="Parrainage — filleuls"
              items={data.filleulsParraines ?? []}
              emptyLabel="Aucun filleul parrainé."
              columns={[
                { header: 'Filleul' },
                { header: 'Code utilisé' },
                { header: 'Date' },
              ]}
              renderRow={(row: Record<string, unknown>) => {
                const filleul = row.filleul as {
                  profil?: { prenom?: string; nom?: string };
                  profilApprenant?: { matricule?: string };
                };
                return (
                  <tr key={String(row.id)}>
                    <td>
                      {formatPersonName(filleul?.profil)}
                      {filleul?.profilApprenant?.matricule ? (
                        <span className="ml-1 text-xs text-on-surface-variant">
                          ({filleul.profilApprenant.matricule})
                        </span>
                      ) : null}
                    </td>
                    <td>{row.codeUtilise as string}</td>
                    <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
                  </tr>
                );
              }}
            />

            <PaginatedTableSection
              icon="monetization_on"
              title="Commissions parrainage"
              items={data.commissionsGagnees ?? []}
              emptyLabel="Aucune commission."
              columns={[
                { header: 'Montant', className: 'text-right' },
                { header: 'Taux' },
                { header: 'Statut' },
                { header: 'Date' },
              ]}
              renderRow={(row: Record<string, unknown>) => (
                <tr key={String(row.id)}>
                  <td className="text-right font-semibold tabular-nums">
                    {formatMoney(row.montantCommission as string | number, 'CDF')}
                  </td>
                  <td>{row.tauxPct as string}%</td>
                  <td>{row.statut as string}</td>
                  <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
                </tr>
              )}
            />
          </div>

          {data.parrainEnregistre ? (
            <AcademicPanelCard icon="supervisor_account" title="Parrainage — mon parrain" variant="accent">
              <DetailGrid
                rows={[
                  {
                    label: 'Parrain',
                    value: formatPersonName(
                      (data.parrainEnregistre as { parrain?: { profil?: { prenom?: string; nom?: string } } })
                        .parrain?.profil,
                    ),
                  },
                  {
                    label: 'Code utilisé',
                    value: (data.parrainEnregistre as { codeUtilise?: string }).codeUtilise || '—',
                  },
                  {
                    label: 'Date',
                    value: formatDate((data.parrainEnregistre as { dateCreation?: string }).dateCreation),
                  },
                ]}
              />
            </AcademicPanelCard>
          ) : null}

          <PaginatedTableSection
            icon="notifications"
            title="Notifications récentes"
            items={data.notifications ?? []}
            emptyLabel="Aucune notification."
            columns={[
              { header: 'Titre' },
              { header: 'Type' },
              { header: 'Date' },
            ]}
            renderRow={(row: Record<string, unknown>) => (
              <tr key={String(row.id)}>
                <td>{row.title as string}</td>
                <td className="text-xs">{row.type as string}</td>
                <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
              </tr>
            )}
          />

          {(data.profil?.documents?.length ?? 0) > 0 ? (
            <PaginatedTableSection
              icon="folder"
              title="Documents"
              items={data.profil.documents}
              emptyLabel="Aucun document."
              columns={[
                { header: 'Type' },
                { header: 'Fichier' },
                { header: 'Date' },
              ]}
              renderRow={(row: Record<string, unknown>) => (
                <tr key={String(row.id)}>
                  <td>{row.typeDocument as string}</td>
                  <td className="max-w-[200px] truncate text-xs">{row.urlFichier as string}</td>
                  <td className="text-xs text-on-surface-variant">{formatDate(row.dateCreation as string)}</td>
                </tr>
              )}
            />
          ) : null}

          {(data.profil?.adresses?.length ?? 0) > 0 ? (
            <AcademicPanelCard icon="location_on" title="Adresses" badge={<span className="academic-panel-badge">{data.profil.adresses.length}</span>}>
              <ul className="academic-panel-list">
                {data.profil.adresses.map((addr: Record<string, unknown>) => (
                  <li key={String(addr.id)} className="academic-panel-list-item">
                    {(addr.adresseComplete as string) ||
                      [addr.commune, addr.quartier, addr.detailsRue, addr.ville].filter(Boolean).join(', ') ||
                      '—'}
                  </li>
                ))}
              </ul>
            </AcademicPanelCard>
          ) : null}

          {(data.profilApprenant?.scoresExcellence?.length ?? 0) > 0 ? (
            <AcademicPanelCard
              icon="emoji_events"
              title="Scores d'excellence"
              variant="success"
              badge={
                <span className="academic-panel-badge academic-panel-badge--success">
                  {data.profilApprenant.scoresExcellence.length}
                </span>
              }
            >
              <div className="space-y-4">
                {data.profilApprenant.scoresExcellence.map((row: Record<string, unknown>) => (
                  <article key={String(row.id)} className="academic-panel-note-card">
                    <div className="academic-panel-note-card__head">
                      <p className="academic-panel-note-card__name">{row.periode as string}</p>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {row.estShortlist ? (
                          <span className="academic-panel-badge">Shortlist</span>
                        ) : null}
                        <span className="font-black text-primary">{String(row.scoreGlobal)}</span>
                        <span className="text-on-surface-variant">
                          rang {row.rang != null ? String(row.rang) : '—'}
                        </span>
                      </div>
                    </div>
                    <div className="academic-panel-note-card__body">
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {(
                          [
                            ['Académique', row.scoreAcademique],
                            ['Présence', row.scorePresence],
                            ['Discipline', row.scoreDiscipline],
                            ['Implication', row.scoreImplicationPedagogique],
                            ['Pratique', row.scorePratique],
                            ['Projets', row.scoreProjets],
                          ] as [string, unknown][]
                        ).map(([label, value]) => (
                          <div key={label} className="academic-panel-note-row">
                            <span className="font-bold text-on-surface-variant">{label}</span>
                            <span className="font-black text-slate-900">{String(value ?? '—')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </AcademicPanelCard>
          ) : null}

          {(data.profilApprenant?.orientationsExcellence?.length ?? 0) > 0 ? (
            <PaginatedTableSection
              icon="work"
              title="Orientations employabilité"
              items={data.profilApprenant.orientationsExcellence}
              emptyLabel="Aucune orientation."
              variant="accent"
              columns={[
                { header: 'Période' },
                { header: 'Opportunité' },
                { header: 'Statut' },
                { header: 'Date', className: 'text-right' },
              ]}
              renderRow={(row: Record<string, unknown>) => {
                const opp = row.opportunite as Record<string, unknown> | undefined;
                const partenaire = opp?.partenaire as { nom?: string } | undefined;
                return (
                  <tr key={String(row.id)}>
                    <td>{row.periode as string}</td>
                    <td>
                      <p className="font-semibold">{opp?.titre as string}</p>
                      {partenaire?.nom ? (
                        <p className="text-xs text-on-surface-variant">{partenaire.nom}</p>
                      ) : null}
                    </td>
                    <td>
                      <span className="academic-panel-badge academic-panel-badge--muted">{row.statut as string}</span>
                    </td>
                    <td className="text-right text-xs text-on-surface-variant">
                      {formatDate(row.dateProposition as string)}
                    </td>
                  </tr>
                );
              }}
            />
          ) : null}

          <div className="flex justify-start pt-1">
            <button
              type="button"
              onClick={() => navigate('/admin/apprenants')}
              className="academic-panel-btn academic-panel-btn--ghost"
            >
              <LucideSymbolIcon name="arrow_back" tone="inherit" size={16} />
              Retour à l&apos;annuaire
            </button>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
}
